// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { Heading } from "@radix-ui/themes";
import { type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { splitHeadlineOnPuntt } from "./homeCopy";

const PUNTT_ACCENT_STYLE: CSSProperties = {
  backgroundImage:
    "linear-gradient(135deg, rgb(var(--puntt-blue-9)), rgb(var(--puntt-blue-11)))",
  WebkitBackgroundClip: "text",
  color: "transparent",
};

type AskV2HeroProps = {
  /** Personalized headline from christo; null keeps the translated default. */
  headline?: string | null;
};

export function AskV2Hero({ headline = null }: AskV2HeroProps) {
  const { t } = useTranslation("ui");
  const { t: tComponents } = useTranslation("components");
  const personalized = headline?.trim() ? headline.trim() : null;
  const split = personalized ? splitHeadlineOnPuntt(personalized) : null;

  return (
    <div
      data-testid="ask-v2-hero"
      className="stagger-children mb-8 flex flex-col items-center gap-3"
    >
      <div className="animate-glow-ring relative z-10 flex size-16 items-center justify-center rounded-2xl bg-puntt-accent-3 shadow-lg">
        <svg
          width="36"
          height="35"
          viewBox="0 0 30 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M22.4939 5.67486H15.8328V2.34435C15.8328 2.12352 15.7451 1.91174 15.589 1.75559C15.4328 1.59944 15.221 1.51172 15.0002 1.51172C14.7794 1.51172 14.5676 1.59944 14.4114 1.75559C14.2553 1.91174 14.1676 2.12352 14.1676 2.34435V5.67486H7.50654C6.62323 5.67486 5.7761 6.02576 5.15151 6.65035C4.52692 7.27494 4.17603 8.12207 4.17603 9.00538V20.6622C4.17603 21.5455 4.52692 22.3926 5.15151 23.0172C5.7761 23.6418 6.62323 23.9927 7.50654 23.9927H22.4939C23.3772 23.9927 24.2243 23.6418 24.8489 23.0172C25.4735 22.3926 25.8244 21.5455 25.8244 20.6622V9.00538C25.8244 8.12207 25.4735 7.27494 24.8489 6.65035C24.2243 6.02576 23.3772 5.67486 22.4939 5.67486Z"
            fill="rgb(var(--puntt-blue-9))"
          />
          <circle
            cx="10.428"
            cy="13.5947"
            r="2.91667"
            stroke="white"
            strokeWidth="1.66667"
          />
          <circle
            cx="19.5947"
            cy="13.5947"
            r="2.91667"
            stroke="white"
            strokeWidth="1.66667"
          />
        </svg>
      </div>
      {/* why: this is the landing page's title — the only other heading on the
          page is the WelcomeBriefing's h2, so rendering it as Radix `Text`
          (a plain span) left the page with no h1 and a heading order that
          starts at level 2. Screen-reader heading navigation skipped the
          title entirely. */}
      <Heading
        as="h1"
        size="8"
        weight="bold"
        className="text-center text-puntt-neutral-gray-12"
        data-testid="ask-v2-hero-title"
        data-personalized={personalized ? "true" : "false"}
      >
        {personalized == null ? (
          <>
            {t("home.ask")}{" "}
            <span style={PUNTT_ACCENT_STYLE}>
              {tComponents("askPuntt.feed.puntt")}
            </span>
          </>
        ) : split == null ? (
          personalized
        ) : (
          <>
            {split.before}
            <span style={PUNTT_ACCENT_STYLE}>{split.accent}</span>
            {split.after}
          </>
        )}
      </Heading>
    </div>
  );
}
